<template>
  <div class="documents-tab p-bottom-4">
    <div v-if="!filteredRequestLoading" class="documents-tab-content">
      <div class="documents-tab-header has-background-light columns is-desktop">
        <div class="column">
          <NrSelect
            :placeholder="setDocVisibilityDropdownText"
            :options="filterDocVisibilitiesByPermission"
            @input="filterByVisibility"
            :hideLabel="true"
            v-model="selectedDocumentVisibility"
          />
        </div>
        <div class="column" v-if="canFilterByRisk">
          <NrSelect
            :placeholder="selectedSeverity.label"
            :options="severities"
            @input="filterBySeverity"
            :hideLabel="true"
            v-model="selectedSeverity"
          />
        </div>
        <div class="column" v-if="canFilterByRisk">
          <NrSelect
            :placeholder="selectedReviewStatus.label"
            :options="reviewStatuses"
            @input="filterByReviewStatus"
            :hideLabel="true"
            v-model="selectedReviewStatus"
          />
        </div>
        <div class="document-pagination column">
          <NrPagination
            simple
            :total="currentDocumentsCount"
            :current="currentPageNum"
            :perPage="perPage"
            icon-prev="chevron-left"
            icon-next="chevron-right"
            aria-next-label="Next page"
            aria-previous-label="Previous page"
            aria-page-label="Page"
            aria-current-label="Current page"
            @change="setPageNum"
          />
        </div>
      </div>
      <div class="documents-tab-content-columns" ref="documents-columns">
        <FoldersSidebar
          :folders="folders"
          :documents="documentList"
          :active-folder="activeFolder"
          :folders-opened="foldersOpened"
          :show-folder-filter="showFolderFilter"
          :limit="perPage"
          v-model="folderSearchTerm"
          @filter-by-folder="filterByFolder"
          @rename-folder="renameFolder"
          @open-folder="openFolder"
          @show-more="showMore"
          @child-show-more="childShowMore"
        />
        <NrModal
          title="Rename folder"
          :show="isFolderRenameActive"
          :disableButtons="invalidFolderName"
          primary-button-text="Save"
          hasSecondaryButton
          @on-primary-click="submitRenameFolder"
          @on-secondary-click="setCurrentAction('')"
        >
          <NrFormInput
            label="Folder rename"
            type="text"
            name="folder"
            required
            v-model="folderToRename"
          />
        </NrModal>
        <div class="cards-column">
          <div
            class="level is-mobile custom-table-header search-results-column"
          >
            <div class="level-left">
              <span>
                <NrIconButton
                  aria-label="Toggle sidebar"
                  variant="minimal-neutral"
                  :icon="{
                    name: 'filter',
                    type: showFolderFilter ? 'regular' : 'solid'
                  }"
                  size="small"
                  @click="showFolderFilter = !showFolderFilter"
                />
              </span>
              <span class="t--overflow-ellipsis">
                <span
                  data-cy="document-count"
                  class="has-text-weight-semibold is-size-5"
                >
                  {{ totalDocumentCountConditional() }}
                </span>
                <span class="p-left-8 active-folder-name">
                  {{ `results in '${activeFilterName}'` }}
                </span>
              </span>
            </div>
          </div>
          <div class="search-and-sort-container">
            <NrFormInput
              class="document-search"
              placeholder="filter by document name"
              label="Filter by document name"
              v-model="docSearchTerm"
            />

            <div id="documents-sort-menu-wrapper" v-click-away="closeSortMenu">
              <NrTooltip position="top" label="Sorting by">
                <NrIconButton
                  :aria-expanded="sortMenuOpen"
                  aria-controls="documents-sort-menu"
                  aria-label="Change sorting method"
                  :icon="{ name: 'bars-sort', type: 'solid' }"
                  variant="minimal-neutral"
                  @click="sortMenuOpen = !sortMenuOpen"
                />
              </NrTooltip>

              <div id="documents-sort-menu" v-show="sortMenuOpen">
                <NrButton
                  class="sort-menu-button"
                  :class="{ 'selected-sort-field': sortField === 'title' }"
                  variant="minimal-neutral"
                  :aria-current="sortField === 'title'"
                  @click="selectSortField('title')"
                >
                  <span>Name</span>
                </NrButton>
                <NrButton
                  class="sort-menu-button"
                  :class="{ 'selected-sort-field': sortField === 'id' }"
                  variant="minimal-neutral"
                  :aria-current="sortField === 'id'"
                  @click="selectSortField('id')"
                >
                  <span>Creation date</span>
                </NrButton>
                <NrButton
                  class="sort-menu-button"
                  :class="{ 'selected-sort-field': sortField === 'state' }"
                  variant="minimal-neutral"
                  :aria-current="sortField === 'state'"
                  @click="selectSortField('state')"
                >
                  <span>Visibility</span>
                </NrButton>
              </div>
            </div>
            <NrTooltip position="top" label="Sorting order">
              <NrIconButton
                aria-label="Change sort order"
                :icon="{
                  name: sortOrder === 'asc' ? 'sort-down' : 'sort-up',
                  type: 'regular'
                }"
                variant="minimal-neutral"
                @click="toggleSortOrder"
              />
            </NrTooltip>
          </div>

          <div class="has-background-white icons-filters is-flex">
            <span id="select-all-documents-label" class="is-sr-only">
              Select all documents on page
            </span>
            <NrFormInput
              label="Select all"
              type="checkbox"
              aria-labelledby="select-all-documents-label"
              class="select-list-checkbox"
              aria-label="Select all documents on page"
              v-model="selectAllDocsOnCurrentPage"
            />
            <span v-for="icon in icons" :key="`nr-${icon.name}-${icon.label}`">
              <NrTooltip :label="icon.label">
                <NrIconButton
                  variant="minimal-neutral"
                  :aria-label="icon.label"
                  :icon="{ name: icon.name, type: 'regular' }"
                  class="bulk-action"
                  @click="handleActions(icon.name)"
                />
              </NrTooltip>
            </span>
            <span v-if="!this.currentUserPresent">
              <NrTooltip
                label="You must be logged in with a NextRequest account to bulk download documents."
              >
                <NrIconButton
                  variant="minimal-neutral"
                  :icon="{ name: 'download', type: 'regular' }"
                  disabled="true"
                />
              </NrTooltip>
            </span>
          </div>

          <div v-if="!docs.length" class="documents-tab-empty">
            <p
              class="level-item is-size-5 has-text-grey has-text-centered task-empty-state"
            >
              {{ emptyStateText }}
            </p>
          </div>
          <DocumentTools
            :pretty-id="request.pretty_id"
            :documents-selected-count="selectedDocIds.length"
            :select-all="selectAll"
            :active-doc-title="activeDocTitle"
            :selected-doc-ids="selectedDocIds"
            :selected-documents="selectedDocs"
            :document-filter-params="docsPayload()"
            @on-visibility-submit="submitDocVisibilityForm"
            @on-expiration-submit="submitDocExpirationForm"
            @on-move-to-folder-submit="submitDocFolderForm"
            @on-doc-delete-submit="submitDocDeleteForm"
            @on-doc-rename-submit="submitDocRenameForm"
          />
          <div>
            <span
              v-if="
                !selectAll &&
                selectedDocIds.length &&
                selectedDocIds.length <= perPage
              "
              class="is-size-7 m-left-12"
            >
              {{ selectedDocIds.length }}
              {{ selectedDocIds.length > 1 ? 'documents' : 'document' }}
              selected.
            </span>
            <NrButton
              size="small"
              variant="minimal-neutral"
              v-if="
                !selectAll &&
                selectAllDocsOnCurrentPage &&
                totalDocumentsCount > perPage
              "
              class="select-all-documents-view-button"
              @click="setSelectAll"
            >
              select all {{ currentDocumentsCount }} documents in this view
            </NrButton>
            <span
              v-if="selectAll && totalDocumentsCount > perPage"
              class="is-size-7 m-left-12"
            >
              {{ currentDocumentsCount }} documents selected. All documents in
              this view are selected.
            </span>
          </div>
          <div class="documents-tab-content-list" v-if="documentList.length">
            <DocumentCard
              ref="docList"
              v-for="(document, index) in documentList"
              :key="`${document.id}-item-${index}`"
              :modelValue="selectedDocIds"
              :document-checked="selectedDocIds.includes(document.id)"
              :documentExpirationLabel="documentExpirationLabel(document)"
              :date="document.upload_date"
              :title="document.title"
              :highlight="getDocCardHighlight(document.visibility)"
              :background="getDocCardBackground(document.visibility)"
              :format="document.file_extension"
              :visibility="document.visibility"
              :id="document.id"
              :folder-name="nofolderFilter(document)"
              :disabled="documentsProcessingIds.includes(document.id)"
              :checkboxDisabled="selectAll"
              :tags="documentTags(document)"
              :pills="documentPills(document)"
              class="document-card-border"
              @update:modelValue="trackSelectedDocs($event, document.id)"
              @handle-click="linkToViewer"
              @handle-tag-click="handleTagClick($event, document)"
            >
              <template v-slot:actions>
                <DocumentCardActions
                  :id="document.id"
                  :can-see-rename-action="canUpdateDocument"
                  :can-see-redact-action="canSeeRedactAction(document.id)"
                  :can-see-change-visibility-action="$can('manage', 'Request')"
                  :can-see-delete-action="canSeeDeleteAction(document.id)"
                  :can-see-move-to-folder-action="
                    $can('read', 'staff_only_items')
                  "
                  :can-see-download-action="true"
                  @on-visibility="
                    selectActiveCard(document.id), openModal('changeVisibility')
                  "
                  @on-download="
                    selectActiveCard(document.id), submitDocDownload()
                  "
                  @on-edit="
                    selectActiveCard(document.id),
                      (activeDocTitle = document.title),
                      openModal('docRename')
                  "
                  @on-redact="submitDocRedact(document.id)"
                  @on-delete="
                    selectActiveCard(document.id), openModal('docDelete')
                  "
                  @on-move="
                    selectActiveCard(document.id), openModal('moveToFolder')
                  "
                />
              </template>
            </DocumentCard>
          </div>
        </div>
        <nr-modal
          primary-button-text="Review document"
          secondary-button-text="Close"
          title="Document risk details"
          :has-secondary-button="true"
          :show="!!activeScan"
          @on-close-click="activeScan = null"
          @on-primary-click="redirectToDocumentPage"
          @on-secondary-click="activeScan = null"
        >
          <risk-findings
            @on-doc-title-click="redirectToDocumentPage"
            :scan="activeScan"
            @on-review-status-click="setDocReview(activeScan)"
          />
        </nr-modal>
      </div>
    </div>
    <NrLoadingAnimation v-else :count="3" />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { addDays } from 'date-fns'
import DocumentTools from '../components/DocumentTools.vue'
import DocumentCard from '../../../vue_shared/components/DocumentCard.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import RiskFindings from '../../admin/components/risk_findings.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import { directive } from 'vue3-click-away'
import FoldersSidebar from './FoldersSidebar.vue'
import DocumentCardActions from './DocumentCardActions.vue'
import NrSentry from '../../../utils/nr_sentry'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import helpers from '../../../utils/helpers'
import setupRiskTags from '../../../utils/setup_risk_tags'
import {
  toggleReviewStatus,
  reviewStatusColor
} from '../../admin/document_review_status'
import removeApplyFilterKeyEventListeners from '../../../mixins/removeApplyFilterKeyEventListeners'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrPagination from '@nextrequestco/components/src/components/NrPagination'

const initialState = () => {
  return {
    activeFolder: 'all',
    selectedDocumentVisibility: { id: 'all', label: 'All Documents' },
    selectedSeverity: { id: null, label: 'All risk levels' },
    selectedReviewStatus: { id: null, label: 'All review types' },
    activeFilterName: 'All Documents',
    docSearchTerm: '',
    currentPageNum: 1,
    foldersOpened: [],
    folderToRename: '',
    documentList: [],
    folderSearchTerm: '',
    filtersApplied: {},
    filteredFolder: '',
    showFolderFilter: true,
    documentVisibilities: [
      { id: 'all', label: 'All documents' },
      { id: 'staff', label: 'Staff only' },
      { id: 'requester', label: 'Requester + staff' },
      { id: 'public', label: 'Public' }
    ],
    severities: [
      { id: null, label: 'All risk levels' },
      { id: 'high', label: 'High risk' },
      { id: 'medium', label: 'Medium risk' },
      { id: 'low', label: 'Low risk' },
      { id: 'na', label: 'Unscannable' },
      { id: 'pending', label: 'Pending' }
    ],
    reviewStatuses: [
      { id: null, label: 'All review types' },
      { id: 'reviewed', label: 'Reviewed' },
      { id: 'needs_review', label: 'Needs review' }
    ],
    activeDocTitle: '',
    singleDocAction: false,
    folderlessDocs: false,
    selectedDocIds: [],
    selectAll: false,
    selectAllDocsOnCurrentPage: false,
    perPage: 25,
    folderAction: null,
    activeScan: null,
    sortMenuOpen: false,
    sortField: localStorage.getItem('documentsSortField') || 'id',
    sortOrder: localStorage.getItem('documentsSortOrder') || 'asc'
  }
}

export default {
  name: 'DocumentsTab',
  components: {
    DocumentTools,
    DocumentCard,
    FoldersSidebar,
    DocumentCardActions,
    NrModal,
    'risk-findings': RiskFindings,
    NrButton,
    NrIconButton,
    NrTooltip,
    NrSelect,
    NrFormInput,
    NrLoadingAnimation,
    NrPagination
  },
  mixins: [removeApplyFilterKeyEventListeners],
  data() {
    return initialState()
  },
  props: {
    open: {
      type: Boolean
    },
    docs: {
      type: Array
    },
    folders: {
      type: Array
    }
  },
  directives: { ClickAway: directive },
  watch: {
    $route(prev, next) {
      if (prev.name !== next.name) {
        return
      }
      if (prev.params.prettyId === next.params.prettyId) {
        return
      }
      // Reset initial document tab state
      Object.assign(this.$data, initialState())
    },

    open(newValue) {
      if (newValue) {
        this.refreshDocuments()
        this.refreshFolders()
      }
    },

    docs() {
      this.documentList = this.docs
      if (
        typeof this.activeFolder === 'string' &&
        this.activeFolder.includes('top')
      ) {
        this.documentList = this.docs.filter(
          (item) => !item.subfolder_name.length
        )
      }
    },

    selectAll(val) {
      this.setCardListCheckbox(val)
    },

    selectAllDocsOnCurrentPage(val) {
      // If selectAllDocsOnCurrentPage changes to true, add the ids to
      // selectedDocIds state
      // If selectAllDocsOnCurrentPage changes to false and not taking action
      // on a single document, deselect all cards on the list
      if (val) {
        this.selectedDocIds = this.documentList.map((doc) => doc.id)
        this.setCardListCheckbox(val)
      } else if (
        !val &&
        !this.singleDocAction &&
        this.selectedDocIds.length === this.documentList.length
      ) {
        this.selectedDocIds = []
        this.setCardListCheckbox(val)
        this.selectAll = false
      }
      // If selectAllDocsOnCurrentPage changes to false and taking action on a single card, ony select the active document
      if (this.singleDocAction && !val) {
        this.onlySelectActiveDoc(this.selectedDocIds[0])
      }
    },
    docSearchTerm() {
      this.currentPageNum = 1
      this.resetCardSelection()
      const filterData = this.docsPayload()
      if (this.docSearchTerm.length > 2) {
        this.setDocumentFilters(filterData)
        this.fetchDocuments(filterData)
      }
      if (this.docSearchTerm.length === 0) {
        this.removeNullFromObject(filterData)
        this.setDocumentFilters(filterData)
        this.fetchDocuments(filterData)
      }
    },
    folderSearchTerm() {
      this.activeFolder = null
      this.foldersOpened = []
      this.refreshFolders()
    },
    request(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetDocumentSelection()
        this.setDocumentFilters([])
        this.stopJobPolling()
      }
    },
    sortField(newVal) {
      localStorage.setItem('documentsSortField', newVal)
      this.refreshDocuments()
    },
    sortOrder(newVal) {
      localStorage.setItem('documentsSortOrder', newVal)
      this.refreshDocuments()
    }
  },
  computed: {
    ...mapState({
      hasPiiScanEnabled: (state) =>
        !!(state.account && state.account.pii_scan_feature_flag),
      hasRetentionEnabled: (state) =>
        !!(state.account && state.account.retention),
      hasRedactionEnabled: (state) =>
        !!(state.account && state.account.redaction),
      currentUser: (state) => state.currentUser
    }),
    ...mapState('request_show', {
      documentsFolders: (state) => state.documentsFolders,
      searchDocumentResultsCount: (state) => state.searchDocumentResultsCount,
      currentDocumentsCount: (state) => state.currentDocumentsCount,
      totalDocumentsCount: (state) => state.totalDocumentsCount,
      documentsStateTimestamp: (state) => state.documentsStateTimestamp,
      request: (state) => state.request,
      selectedChangeDocVisibility: (state) => state.selectedChangeDocVisibility,
      messageToRequester: (state) => state.messageToRequester,
      currentAction: (state) => state.currentAction,
      currentDocumentFilters: (state) => state.currentDocumentFilters,
      documentsProcessingIds: (state) => state.documentsProcessingIds,
      documents: (state) => state.documents,
      filteredRequestLoading: (state) => state.filteredRequestLoading,
      jobPollIntervalId: (state) => state.jobPollIntervalId
    }),

    canUpdateDocument() {
      if (
        this.currentUser?.user_role === 'staff' &&
        this.request.visibility === 'department_published'
      ) {
        return false
      }

      return this.$ability.can('update', 'Document')
    },

    icons() {
      return [
        { name: 'eye', label: 'Change document visibility' },
        { name: 'folder', label: 'Move to folder' },
        { name: 'download', label: 'Download documents' },
        { name: 'trash-alt', label: 'Delete documents' },
        { name: 'archive', label: 'Change document expiration status' }
      ].filter((icon) => this.iconVisible(icon.name))
    },

    isFolderRenameActive() {
      return this.currentAction === 'renameFolder'
    },

    canReadStaffOnlyItems() {
      return this.$ability.can('read', 'staff_only_items')
    },

    canManageRequest() {
      return this.$ability.can('manage', 'Request')
    },

    setDocVisibilityDropdownText() {
      const visibility = this.documentVisibilities.find((val) => {
        return val.id === this.selectedDocumentVisibility.id
      })
      if (visibility) {
        return visibility.label
      }
      return 'All documents'
    },

    filterDocVisibilitiesByPermission() {
      if (
        this.currentUserPresent &&
        this.request.requester.id === this.$store.state.currentUser.id
      ) {
        return this.documentVisibilities.filter((visi) => {
          return visi.id !== 'staff'
        })
      }
      return this.documentVisibilities
    },

    canFilterByRisk() {
      return this.hasPiiScanEnabled && this.canReadStaffOnlyItems
    },

    invalidFolderName() {
      return this.folderToRename === ''
    },
    selectedDocs() {
      return this.documentList.filter(({ id }) =>
        this.selectedDocIds.includes(id)
      )
    },
    hasFilters() {
      return (
        this.selectedDocumentVisibility.id !== 'all' ||
        this.selectedSeverity.id !== null ||
        this.selectedReviewStatus.id !== null ||
        this.activeFolder !== 'all' ||
        this.folderSearchTerm !== '' ||
        this.docSearchTerm !== ''
      )
    },
    emptyStateText() {
      return this.hasFilters
        ? 'No documents found with the applied filters'
        : 'No more documents on this request'
    },
    canSeeStaffOnlyItems() {
      return this.$can('read', 'staff_only_items')
    },
    canSeeAdminOnlyItems() {
      return this.$can('read', 'admin_only_items')
    }
  },
  methods: {
    ...mapActions('request_show', [
      'fetchDocumentsChildFolders',
      'fetchDocumentsFolders',
      'fetchDocuments',
      'submitBulkDocumentAction',
      'pollBulkJobs',
      'setDocumentFilters',
      'sendNotification',
      'downloadDocuments',
      'fetchJobStatuses',
      'updateDocTitle',
      'fetchTimeline',
      'setCurrentAction',
      'updateFolder',
      'addDocument',
      'setDocumentsProcessingIds'
    ]),
    ...mapActions('admin', ['setDocReviewStatus']),
    ...mapMutations('request_show', {
      setJobPollIntervalId: 'setJobPollIntervalId'
    }),
    resetDocumentSelection() {
      this.selectAll = false
      this.selectAllDocsOnCurrentPage = false
      this.selectedDocIds = []
      this.activeFilterName = 'All Documents'
      this.currentPageNum = 1
      this.activeFolder = 'all'
      this.selectedDocumentVisibility = { id: 'all', label: 'All documents' }
      this.selectedSeverity = { id: null, label: 'All risk levels' }
      this.selectedReviewStatus = { id: null, label: 'All review types' }
      this.docSearchTerm = ''
    },
    canSeeRedactAction(id) {
      const document = this.documentList.find((item) => item.id === id)
      const visibility = document ? document.visibility : null
      return (
        this.canReadStaffOnlyItems &&
        visibility === 'Staff Only' &&
        this.hasRedactionEnabled
      )
    },
    canSeeDeleteAction(id) {
      const document = this.documentList.find((item) => item.id === id)
      const visibility = document ? document.visibility : null
      return (
        this.$can('manage', 'Request') ||
        (this.canReadStaffOnlyItems && visibility === 'Staff Only')
      )
    },
    handleTagClick(tag, doc) {
      if (!doc.document_scan) return
      if (tag.tag_name === 'review_status') {
        this.setDocReview(doc.document_scan)
      } else {
        this.activeScan = doc.document_scan
      }
    },

    handleActions(val) {
      switch (val) {
        case 'eye':
          this.openModal('changeVisibility')
          break
        case 'folder':
          this.openModal('moveToFolder')
          break
        case 'download':
          this.submitDocDownload()
          break
        case 'trash-alt':
          this.openModal('docDelete')
          break
        case 'archive':
          this.openModal('changeExpiration')
          break
        default:
      }
    },

    documentExpirationLabel(document) {
      if (
        this.request.request_state === 'Closed' &&
        this.canSeeStaffOnlyItems &&
        this.hasRetentionEnabled
      ) {
        if (document.exempt_from_retention || !document.expiration_date) {
          return 'Expiration: Never'
        } else {
          return 'Expiration: ' + document.expiration_date
        }
      }
    },

    iconVisible(icon) {
      switch (icon) {
        case 'eye':
          return this.canManageRequest
        case 'folder':
          return this.canSeeStaffOnlyItems
        case 'trash-alt':
          return this.canManageRequest
        case 'download':
          return this.currentUserPresent
        case 'archive':
          return this.hasRetentionEnabled && this.canSeeAdminOnlyItems
        default:
          return false
      }
    },

    setDocReview(scan) {
      const params = {
        id: scan.id,
        reviewStatus: toggleReviewStatus(scan.review_status)
      }
      this.setDocReviewStatus(params).then(() => {
        let doc = this.documents.find((doc) => doc.id === scan.document_id)
        doc = Object.assign({}, doc, {
          document_scan: {
            ...doc.document_scan,
            review_status: toggleReviewStatus(scan.review_status)
          }
        })
        this.addDocument(doc)
        if (!this.activeScan) return
        this.activeScan = Object.assign({}, scan, {
          review_status: toggleReviewStatus(scan.review_status),
          tag_color: reviewStatusColor(scan.review_status)
        })
      })
    },

    redirectToDocumentPage() {
      window.location.pathname = `${this.activeScan.document_path}`
    },
    documentTags(doc) {
      if (!this.hasPiiScanEnabled) return

      if (this.canSeeStaffOnlyItems) {
        return setupRiskTags(doc, this.canReadStaffOnlyItems)
      }
    },
    documentPills(doc) {
      const pills = []

      if (this.canSeeStaffOnlyItems && doc.has_draft_redactions) {
        pills.push({
          label: doc.has_comments ? 'Draft + comments' : 'Draft',
          size: 'small',
          color: 'gray'
        })
      }

      if (this.canSeeStaffOnlyItems && doc.has_redactions) {
        pills.push({
          label: 'Redacted',
          size: 'small',
          color: 'blue'
        })
      }
      return pills
    },
    nofolderFilter(val) {
      if (val.subfolder_name === '') {
        return val.folder_name
      } else {
        return val.subfolder_name
      }
    },

    submitDocFolderForm({ name, folder_id: folderId }) {
      const payload = {
        documents_state_timestamp: this.documentsStateTimestamp,
        all_selected: this.selectAll ? this.selectAll : null,
        folderless_docs: this.folderlessDocs ? this.folderlessDocs : null,
        doc_ids: this.selectAll ? null : this.selectedDocIds,
        request_id: this.request.pretty_id,
        search_term: this.docSearchTerm === '' ? null : this.docSearchTerm,
        parent_folder_id: this.filtersApplied.parent_folder_id,
        child_folder_id: this.filtersApplied.child_folder_id,
        visibility: this.selectedDocumentVisibility.id
      }

      // folderId is -1 if "No folder" has been selected
      if (folderId === -1) {
        payload.bulk_action = 'remove_from_folder'
      } else {
        payload.bulk_action = 'move_to_folder'
        payload.folder_id = folderId
      }

      this.removeNullFromObject(payload)
      this.setDocumentsProcessingIds(this.selectedDocIds)
      this.submitBulkDocumentAction(payload)
        .then((xhr) => {
          if (xhr.status === 200) {
            const data = xhr.response ? xhr.response.body.data : xhr.data
            const jobId = data.jobId
            const docAmount = this.selectAll
              ? 'all'
              : this.selectedDocIds.length
            if (!jobId) {
              this.$toasted.error(
                `Moving ${docAmount} document(s) to folder has failed to start.`
              )
              return
            }

            const jobArgs = {
              docIds: this.selectedDocIds,
              selectAll: this.selectAll,
              jobId,
              currentFolderId: folderId,
              parentFolderId: this.currentDocumentFilters.parent_folder_id,
              filteredFolderType: this.currentDocumentFilters.child_folder_id
                ? 'subfolder'
                : 'top folder',
              folderName: name,
              expiry: addDays(new Date(), 2)
            }
            helpers.setPendingJobInLocalStorage(
              `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowMoveFolder`,
              jobArgs
            )
            this.$toasted.show(`Moving ${docAmount} document(s) to a folder.`)
            this.pollBulkActions()
          }
        })
        .catch((err) => {
          if (err.response.status === 409) {
            this.setDocumentsProcessingIds([])
            this.$toasted.error(err.response.data.message)
          } else {
            const docAmount = this.selectAll
              ? 'all'
              : this.selectedDocIds.length
            this.$toasted.error(
              `An error has occurred while moving ${docAmount} document(s) to a folder.`
            )
          }
        })
      this.setCurrentAction('')
    },
    setupFolderRenamePayload() {
      const parentFolderId = this.filtersApplied.parent_folder_id
      const payload = {
        request_id: this.request.pretty_id,
        folder_name: this.folderToRename,
        parent_folder_id: parentFolderId,
        current_folder_id:
          parseInt(parentFolderId) < parseInt(this.activeFolder)
            ? parseInt(this.activeFolder.replace(parentFolderId, ''))
            : parseInt(this.activeFolder)
      }
      if (this.filteredFolder === 'subfolder') {
        payload.child_folder_id = parseInt(
          this.activeFolder.replace(parentFolderId, '')
        )
      }
      return payload
    },
    refreshFolders() {
      return this.fetchDocumentsFolders({
        request_id: this.request.pretty_id,
        visibility: this.selectedDocumentVisibility.id,
        search_text:
          this.folderSearchTerm.length > 2 ? this.folderSearchTerm : null
      })
    },
    resetParentFoldersAndFilter(payload) {
      const folderData = {
        parent_folder_id: payload.parent_folder_id,
        activeId: payload.current_folder_id,
        folderName: payload.folder_name,
        type: 'top folder'
      }
      this.refreshFolders().then(() => {
        this.foldersOpened = []
        setTimeout(() => {
          const newParentFolder = this.documentsFolders.filter((fol) => {
            return fol.folder_name === payload.folder_name
          })
          if (newParentFolder.length) {
            const newParentFolderId = newParentFolder[0].id
            folderData.parent_folder_id = newParentFolderId
            folderData.activeId = newParentFolderId
            this.filterByFolder(folderData)
          } else {
            folderData.type = 'all'
            folderData.folderName = 'All Documents'
            this.filterByFolder(folderData)
          }
        }, 1000)
      })
    },
    removeChildFolder(parentFolder, childFolderId) {
      const childFolders = parentFolder.folders.filter((fol) => {
        return fol.id !== childFolderId
      })
      parentFolder.folders = childFolders
    },
    resetChildFoldersAndFilter(payload) {
      const folderData = {
        parent_folder_id: payload.parent_folder_id,
        activeId: payload.parent_folder_id,
        type: 'top folder'
      }
      this.fetchDocumentsChildFolders({
        request_id: this.request.pretty_id,
        visibility: this.selectedDocumentVisibility.id,
        folder_id: payload.parent_folder_id
      }).then(() => {
        const parentFolder = this.getParentFolder(payload.parent_folder_id)
        this.removeChildFolder(parentFolder, payload.current_folder_id) // Removes old child folder
        setTimeout(() => {
          folderData.folderName = parentFolder.folder_name
          this.filterByFolder(folderData) // Resets parent folder filter
        }, 1000)
      })
    },
    resetFoldersAfterRenaming(payload, filteredFolderType) {
      filteredFolderType === 'subfolder'
        ? this.resetChildFoldersAndFilter(payload)
        : this.resetParentFoldersAndFilter(payload)
    },
    pollFolderRenameJob(jobId, folderData, filteredFolderType) {
      const folderRenameJob = setInterval(() => {
        this.fetchJobStatuses({
          pretty_id: this.request.pretty_id
        })
          .then(({ data }) => {
            const jobs = data.jobs
            jobs.forEach((job) => {
              if (
                helpers.backgroundJobComplete(job.status) &&
                job.job_type === 'process_folders' &&
                job.id === jobId
              ) {
                this.$toasted.success('Successfully renamed folder name.')
                this.resetFoldersAfterRenaming(folderData, filteredFolderType)
                clearInterval(folderRenameJob)
              }
              if (
                helpers.backgroundJobFailed(job.status) &&
                job.job_type === 'process_folders' &&
                job.id === jobId
              ) {
                this.$toasted.error(
                  'An error has occurred while renaming folder name.'
                )
                clearInterval(folderRenameJob)
              }
            })
          })
          // eslint-disable-next-line no-unused-vars
          .catch((_err) => {
            clearInterval(folderRenameJob)
          })
      }, 1000)
    },
    submitRenameFolder() {
      this.setCurrentAction('')
      const payload = this.setupFolderRenamePayload()
      const filteredFolderType = this.filteredFolder
      this.updateFolder(payload)
        .then(({ status, data }) => {
          const jobId = data.jobId
          if (status !== 200) {
            return
          }
          this.pollFolderRenameJob(jobId, payload, filteredFolderType)
        })
        // eslint-disable-next-line no-unused-vars
        .catch((_err) => {
          this.$toasted.error(
            'An error has occurred while renaming folder name.'
          )
        })
    },
    getParentFolder(parentFolderId) {
      const parentFolder = this.documentsFolders.filter((fol) => {
        return fol.id === parentFolderId
      })
      return parentFolder[0]
    },
    getChildFolder(parentFolder, childFolderId) {
      const childFolder = parentFolder.folders.filter((fol) => {
        return fol.id === childFolderId
      })
      return childFolder[0]
    },
    getDocCardHighlight(visibility) {
      switch (visibility) {
        case 'Public':
          return 'green'
        case 'Requester':
          return 'blue-100'
        default:
          return ''
      }
    },
    getDocCardBackground(visibility) {
      switch (visibility) {
        case 'Public':
          return 'green'
        case 'Requester':
          return 'purple'
        default:
          return ''
      }
    },
    showMore(pageNum) {
      this.fetchDocumentsFolders({
        request_id: this.request.pretty_id,
        visibility: this.selectedDocumentVisibility.id,
        search_text:
          this.folderSearchTerm.length > 2 ? this.folderSearchTerm : null,
        page_number: pageNum
      })
    },
    // eslint-disable-next-line no-unused-vars
    childShowMore(params) {
      const parentId = params.parent_folder_id
      const pageNum = params.childPageNum
      this.fetchDocumentsChildFolders({
        request_id: this.request.pretty_id,
        visibility: this.selectedDocumentVisibility.id,
        folder_id: parentId,
        page_number: pageNum
      })
    },
    renameFolder(folderName) {
      this.folderToRename = folderName
      this.setCurrentAction('renameFolder')
    },
    filterByFolder(data) {
      this.currentPageNum = 1
      this.activeFilterName = data.folderName
      this.resetCardSelection()
      if (this.activeFolder === data.child_folder_id) {
        return
      } else if (this.activeFolder === data.type) {
        return
      } else {
        this.activeFolder = data.activeId
      }
      this.filteredFolder = data.type
      if (data.type === 'all') {
        this.activeFolder = 'all'
        this.folderlessDocs = false
        this.filtersApplied = {
          parent_folder_id: data.parent_folder_id,
          child_folder_id: null
        }
      } else if (data.type === 'nofolder') {
        this.activeFolder = 'nofolder'
        this.folderlessDocs = true
        this.filtersApplied = {
          parent_folder_id: null,
          child_folder_id: null
        }
      } else if (data.type === 'all folder') {
        this.activeFolder = data.activeId
        this.folderlessDocs = false
        this.filtersApplied = {
          parent_folder_id: data.parent_folder_id,
          child_folder_id: null
        }
      } else if (data.type === 'top folder') {
        this.activeFolder = data.activeId
        this.folderlessDocs = false
        this.filtersApplied = {
          parent_folder_id: data.parent_folder_id,
          child_folder_id: null
        }
      } else if (data.type === 'subfolder') {
        this.folderlessDocs = false
        this.filtersApplied = {
          parent_folder_id: data.parent_folder_id,
          child_folder_id: data.child_folder_id
        }
      }
      const filterData = this.docsPayload()
      this.removeNullFromObject(filterData)
      this.setDocumentFilters(filterData)
      this.fetchDocuments(filterData)
    },
    openFolder({ index, id }) {
      const parentFolder = this.getParentFolder(id)
      const noExistingChildFolders =
        !parentFolder.folders && parentFolder.has_child_folders
      if (noExistingChildFolders) {
        this.fetchDocumentsChildFolders({
          request_id: this.request.pretty_id,
          visibility: this.selectedDocumentVisibility.id,
          folder_id: id
        })
      }
      this.docsFolders = this.documentsFolders
      if (this.foldersOpened.includes(index)) {
        const location = this.foldersOpened.indexOf(index)
        this.foldersOpened.splice(location, 1)
      } else {
        this.foldersOpened.push(index)
      }
    },
    totalDocumentCountConditional() {
      if (this.searchDocumentResultsCount >= 0) {
        return `${this.currentDocumentsCount} / ${this.totalDocumentsCount}`
      }

      return this.totalDocumentsCount
    },
    setupDocVisibilityPayload(params) {
      let payload
      payload = {
        documents_state_timestamp: this.documentsStateTimestamp,
        all_selected: this.selectAll ? this.selectAll : null,
        folderless_docs: this.folderlessDocs ? this.folderlessDocs : null,
        doc_ids: this.selectAll ? null : this.selectedDocIds,
        bulk_action: 'update_visibility',
        request_id: this.request.pretty_id,
        search_term: this.docSearchTerm === '' ? null : this.docSearchTerm,
        new_visibility: this.selectedChangeDocVisibility.id,
        visibility:
          this.selectedDocumentVisibility.id === 'all'
            ? null
            : this.selectedDocumentVisibility.id
      }
      payload = { ...payload, ...this.filtersApplied }
      if (params.messageToRequester) {
        Object.assign(payload, {
          message: params.messageToRequester,
          message_template_ids: params.messageTemplateIds
        })
      }
      this.removeNullFromObject(payload)
      return payload
    },
    submitDocVisibilityForm(params) {
      const docVisibilityPayload = this.setupDocVisibilityPayload(params)
      this.setDocumentsProcessingIds(this.selectedDocIds)
      this.submitBulkDocumentAction(docVisibilityPayload)
        .then((xhr) => {
          if (xhr.status === 200) {
            const data = xhr.response ? xhr.response.body.data : xhr.data
            const jobId = data.jobId[0]
            if (!jobId) {
              this.$toasted.error(
                'Changing visibility on documents failed to start.'
              )
              return
            }
            const jobArgs = {
              jobId,
              selectedVisibility: params.selectedDocumentVisibility,
              selectAll: this.selectAll,
              docIds: this.selectedDocIds,
              expiry: addDays(new Date(), 2)
            }
            helpers.setPendingJobInLocalStorage(
              `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeVisibility`,
              jobArgs
            )
            this.$toasted.success(
              `Changing visibility on
            ${this.selectAll ? 'all' : this.selectedDocIds.length} ${
              this.selectedDocIds.length === 1 ? 'document' : 'documents'
            }`
            )
            this.pollBulkActions()
          }
        })
        .catch((err) => {
          if (err.response.status === 409) {
            this.setDocumentsProcessingIds([])
            this.$toasted.error(err.response.data.message)
          } else {
            this.$toasted.error(
              `An error has occured while changing visibility on
            ${this.selectAll ? 'all' : this.selectedDocIds.length} ${
              this.selectedDocIds.length === 1 ? 'document' : 'documents'
            }`
            )
          }
        })
      this.setCurrentAction('')
    },
    submitDocExpirationForm(params) {
      const payload = {
        documents_state_timestamp: this.documentsStateTimestamp,
        all_selected: this.selectAll ? this.selectAll : null,
        folderless_docs: this.folderlessDocs ? this.folderlessDocs : null,
        doc_ids: this.selectAll ? null : this.selectedDocIds,
        bulk_action: 'update_exempt_from_retention',
        request_id: this.request.pretty_id,
        search_term: this.docSearchTerm === '' ? null : this.docSearchTerm,
        exempt_from_retention: params.selectedDocumentExpirationStatus
      }
      this.removeNullFromObject(payload)
      this.setDocumentsProcessingIds(this.selectedDocIds)
      this.submitBulkDocumentAction(payload)
        .then((xhr) => {
          if (xhr.status === 200) {
            const data = xhr.response ? xhr.response.body.data : xhr.data
            const jobId = data.jobId[0]
            if (!jobId) {
              this.$toasted.error(
                'Changing expiration status on documents failed to start.'
              )
              return
            }
            const jobArgs = {
              jobId,
              selectedDocumentExpirationStatus:
                params.selectedDocumentExpirationStatus,
              selectAll: this.selectAll,
              docIds: this.selectedDocIds,
              expiry: addDays(new Date(), 2)
            }
            helpers.setPendingJobInLocalStorage(
              `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeExpiration`,
              jobArgs
            )
            this.$toasted.success(
              `Changing expiration status on
            ${this.selectAll ? 'all' : this.selectedDocIds.length} ${
              this.selectedDocIds.length === 1 ? 'document' : 'documents'
            }`
            )
            this.pollBulkActions()
          }
        })
        .catch((err) => {
          if (err.response.status === 409) {
            this.setDocumentsProcessingIds([])
            this.$toasted.error(err.response.data.message)
          } else {
            this.$toasted.error(
              `An error has occured while changing expiration status on
            ${this.selectAll ? 'all' : this.selectedDocIds.length} ${
              this.selectedDocIds.length === 1 ? 'document' : 'documents'
            }`
            )
          }
        })
      this.setCurrentAction('')
    },
    setupBulkToastMsg(jobs) {
      jobs.forEach((job) => {
        if (helpers.backgroundJobFailed(job.status)) {
          if (job.job_type === 'visibility_updater') {
            this.setupFailedChangeVisibilityToastMsg(job.id)
            this.setDocumentsProcessingIds([])
          }
          if (job.job_type === 'exempt_from_retention_updater') {
            this.setupFailedChangeExpirationToastMsg(job.id)
            this.setDocumentsProcessingIds([])
          }
          if (job.job_type === 'flatten') {
            this.setupFailedFlattenToastMsg(job.id)
            this.setDocumentsProcessingIds([])
          }
          if (job.job_type === 'delete_documents') {
            this.setupFailedDeleteToastMsg(job.id)
            this.setDocumentsProcessingIds([])
          }
          if (job.job_type === 'process_folders') {
            this.setupFailedMoveFolderToastMsg(job.id)
            this.setDocumentsProcessingIds([])
          }
        } else if (helpers.backgroundJobComplete(job.status)) {
          if (job.job_type === 'visibility_updater') {
            this.setupSuccessChangeVisibilityToastMsg(job.id)
            this.setDocumentsProcessingIds([])
            this.refreshDocuments()
            this.fetchTimeline({
              pretty_id: this.request.pretty_id,
              filter_categores: []
            })
          }
          if (job.job_type === 'exempt_from_retention_updater') {
            this.setupSuccessChangeExpirationToastMsg(job.id)
            this.setDocumentsProcessingIds([])
            this.refreshDocuments()
            this.fetchTimeline({
              pretty_id: this.request.pretty_id,
              filter_categores: []
            })
          }
          if (job.job_type === 'flatten') {
            this.setupSuccessFlattenToastMsg(job.id)
            this.setDocumentsProcessingIds([])
            this.refreshFolders()
            this.fetchTimeline({
              pretty_id: this.request.pretty_id,
              filter_categores: []
            })
            this.refreshDocuments()
          }
          if (job.job_type === 'delete_documents') {
            this.removeDeleteJob(job.id)
            this.setDocumentsProcessingIds([])
            this.refreshFolders()
            this.fetchTimeline({
              pretty_id: this.request.pretty_id,
              filter_categores: []
            })
            this.refreshDocuments()
          }
          if (job.job_type === 'process_folders') {
            this.setDocumentsProcessingIds([])
            this.setupSuccessMoveFolderToastMsg(job, this.folderAction)
          }
        }
      })
    },
    setupFailedDownloadToastMsg(jobId) {
      this.$toasted.error('An error has occured while zipping the file(s).')
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowDownload`,
        jobId
      )
    },
    setupFailedFlattenToastMsg(jobId) {
      const job = helpers.getPendingJob(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowFlatten`,
        jobId
      )
      this.$toasted.error(
        `Failed to flatten ${job.selectAll ? 'all' : job.docIds.length} ${
          job.docIds.length === 1 ? 'document' : 'documents'
        }.`
      )
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowFlatten`,
        jobId
      )
    },
    setupSuccessFlattenToastMsg(jobId) {
      const job = helpers.getPendingJob(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowFlatten`,
        jobId
      )
      this.$toasted.success(
        `Successfully flattened ${job.selectAll ? 'all' : job.docIds.length} ${
          job.docIds.length === 1 ? 'document' : 'documents'
        }.`
      )
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowFlatten`,
        jobId
      )
    },
    setupFailedDeleteToastMsg(jobId) {
      const job = helpers.getPendingJob(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowDelete`,
        jobId
      )
      this.$toasted.error(
        `Failed to delete ${job.selectAll ? 'all' : job.docIds.length} ${
          job.docIds.length === 1 ? 'document' : 'documents'
        }.`
      )
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowDelete`,
        jobId
      )
    },
    removeDeleteJob(jobId) {
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowDelete`,
        jobId
      )
    },
    setupSuccessMoveFolderToastMsg(job, folderAction) {
      const jobPending = helpers.getPendingJob(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowMoveFolder`,
        job.id
      )
      const docAmount = jobPending.selectAll ? 'All' : jobPending.docIds.length
      if (folderAction === 'removing') {
        this.$toasted.success(`${docAmount} document(s) removed from folder`)
      } else {
        this.$toasted.success(`${docAmount} document(s) moved to a folder`)
      }
      const payload = {
        parent_folder_id: jobPending.parentFolderId,
        current_folder_id: jobPending.currentFolderId,
        folder_name: jobPending.folderName
      }
      this.resetParentFoldersAndFilter(payload)
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowMoveFolder`,
        job.id
      )
    },
    setupFailedChangeVisibilityToastMsg(jobId) {
      this.$toasted.error('Failed to change document retention status')
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeVisibility`,
        jobId
      )
    },
    setupFailedChangeExpirationToastMsg(jobId) {
      this.$toasted.error(
        'An error has occurred while changing expiration on document(s).'
      )
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeExpiration`,
        jobId
      )
    },
    setupFailedMoveFolderToastMsg(jobId) {
      this.$toasted.error(
        'An error has occured when moving documents(s) to folder.'
      )
      helpers.removePendingJobFromLocalStorage(
        `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowMoveFolder`,
        jobId
      )
    },
    setupSuccessChangeVisibilityToastMsg(jobId) {
      const objectName = `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeVisibility`
      const job = helpers.getPendingJob(objectName, jobId)
      if (!job) {
        return
      }
      const selectedDocIds = job.docIds
      const selectAll = job.selectAll

      const count = selectAll ? 'all' : selectedDocIds?.length
      const documentsWord =
        selectedDocIds?.length === 1 ? 'document' : 'documents'

      let message = 'Successfully changed visibility on document(s)'
      if (job.selectedVisibility === 'requester') {
        message = `Successfully released ${count} ${documentsWord} to the requester`
      } else if (job.selectedVisibility === 'public') {
        message = `Successfully published ${count} ${documentsWord}`
      } else if (selectAll || selectedDocIds.length) {
        message = `Successfully changed visibility on ${count} ${documentsWord}`
      }

      this.$toasted.success(message)
      helpers.removePendingJobFromLocalStorage(objectName, jobId)
    },
    setupSuccessChangeExpirationToastMsg(jobId) {
      const objectName = `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowChangeExpiration`
      const job = helpers.getPendingJob(objectName, jobId)
      if (!job) {
        return
      }
      const selectedDocIds = job.docIds
      const selectAll = job.selectAll

      const count = selectAll ? 'all' : selectedDocIds?.length
      const documentsWord =
        selectedDocIds?.length === 1 ? 'document' : 'documents'

      const message = `Successfully changed retention status for ${count} ${documentsWord}`

      this.$toasted.success(message)
      helpers.removePendingJobFromLocalStorage(objectName, jobId)
    },
    submitDocRedact(id) {
      window.location.pathname = `/redaction/${id}`
    },
    submitDocDownload() {
      if (!this.selectedDocIds.length && !this.selectAll) {
        this.setCurrentAction('docEmpty')
        return
      }

      if (this.selectedDocIds.length === 1) {
        this.downloadDocuments({ docId: this.selectedDocIds[0] })
        this.$toasted.success(
          'Document will download automatically when ready.'
        )
        return
      }

      let payload
      if (this.selectAll) {
        payload = {
          documents_state_timestamp: this.documentsStateTimestamp,
          request_id: this.request.pretty_id,
          bulk_action: 'download',
          all_selected: true,
          visibility: this.selectedDocumentVisibility.id,
          search_term:
            this.docSearchTerm.length > 2 ? this.docSearchTerm : null,
          folderless_docs: this.folderlessDocs ? this.folderlessDocs : null,
          ...this.filtersApplied
        }
      }

      if (this.selectedDocIds.length && !this.selectAll) {
        payload = {
          documents_state_timestamp: this.documentsStateTimestamp,
          request_id: this.request.pretty_id,
          bulk_action: 'download',
          doc_ids: this.selectedDocIds
        }
      }
      this.removeNullFromObject(payload)
      this.submitBulkDocumentAction(payload)
        .then((xhr) => {
          if (xhr.status === 200) {
            // Start polling
            const data = xhr.response ? xhr.response.body.data : xhr.data
            const jobId = data.jobId[0]
            if (!jobId) {
              this.$toasted.error('Download failed to start.')
              return
            }
            helpers.setPendingJobInLocalStorage(
              `${this.$store.state.currentUser.id}${this.request.pretty_id}zipFiles`,
              {
                jobId,
                docIds: this.selectedDocIds,
                jobType: 'bulk_download',
                expiry: addDays(new Date(), 2)
              }
            )
            helpers.setJobIdInSessionStorage(jobId)
            this.$toasted.success(
              'Documents will download automatically when ready.'
            )
          }
        })
        // eslint-disable-next-line no-unused-vars
        .catch((err) => {
          if (err.response.status === 409) {
            this.setDocumentsProcessingIds([])
            this.$toasted.error(err.response.data.message)
          } else {
            const nrError = new Error(
              'An error has occurred while downloading',
              {
                cause: err
              }
            )
            NrSentry.captureError(nrError)
            this.$toasted.error('An error has occurred while downloading')
          }
        })
    },
    pollBulkActions() {
      // If there is already an interval running then don't start a new one.
      if (this.jobPollIntervalId) {
        return
      }

      const intervalId = setInterval(() => {
        if (!this.request?.pretty_id) {
          this.stopJobPolling()
          return
        }

        this.fetchJobStatuses({
          pretty_id: this.request.pretty_id
        })
          .then((response) => {
            if (!this.request?.pretty_id) {
              this.stopJobPolling()
              return
            }

            const processingDocIds = helpers.fetchAllProcessingDocIds(
              this.$store.state.currentUser.id,
              this.request.pretty_id,
              'request_show'
            )
            if (processingDocIds.length === 0) {
              this.stopJobPolling()
              return
            }

            const jobs = response.data.jobs
            if (typeof jobs !== 'undefined' || jobs.length !== 0) {
              this.setupBulkToastMsg(jobs)
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch((_err) => {
            this.stopJobPolling()
          })
      }, 2000)
      this.setJobPollIntervalId(intervalId)
    },
    stopJobPolling() {
      this.setJobPollIntervalId(null)
    },
    submitDocDeleteForm() {
      this.setCurrentAction('')
      const payload = {
        documents_state_timestamp: this.documentsStateTimestamp,
        request_id: this.request.pretty_id,
        bulk_action: 'archive',
        all_selected: this.selectAll ? this.selectAll : null,
        doc_ids: this.selectAll ? null : this.selectedDocIds,
        visibility: this.selectedDocumentVisibility.id,
        search_term: this.docSearchTerm.length > 2 ? this.docSearchTerm : null,
        folderless_docs: this.folderlessDocs ? this.folderlessDocs : null,
        ...this.filtersApplied
      }
      this.removeNullFromObject(payload)
      this.setDocumentsProcessingIds(this.selectedDocIds)
      this.submitBulkDocumentAction(payload)
        .then((xhr) => {
          if (xhr.status === 200) {
            const jobId = xhr.data.jobId
            if (!jobId) {
              this.$toasted.error('Deleting document(s) failed to start.')
              return
            }

            helpers.setPendingJobInLocalStorage(
              `${this.$store.state.currentUser.id}${this.request.pretty_id}requestShowDelete`,
              {
                docIds: this.selectedDocIds,
                selectAll: this.selectAll,
                jobId,
                expiry: addDays(new Date(), 2)
              }
            )
            this.pollBulkActions()
          }
        })
        // eslint-disable-next-line no-unused-vars
        .catch((err) => {
          if (err.response.status === 409) {
            this.setDocumentsProcessingIds([])
            this.$toasted.error(err.response.data.message)
          } else {
            this.$toasted.error(
              `An error has occured while deleting ${
                this.selectAll ? 'all' : this.selectedDocIds.length
              } ${this.selectedDocIds.length === 1 ? 'document' : 'documents'}`
            )
          }
        })
    },
    submitDocRenameForm(val) {
      this.setCurrentAction('')
      const payload = { id: this.selectedDocIds[0], title: val }
      this.updateDocTitle(payload).then(() => {
        this.resetCardSelection()
      })
    },
    linkToViewer(id) {
      window.open(`/documents/${id}`, '_blank')
    },
    updateFilters(filterData) {
      this.currentPageNum = 1
      this.activeFolder = 'all'
      filterData.parent_folder_id = null
      filterData.child_folder_id = null
      this.removeNullFromObject(filterData)
      this.resetCardSelection()
      this.setDocumentFilters(filterData)
      this.fetchDocuments(filterData)
      this.fetchDocumentsFolders(filterData)
    },
    filterByVisibility(visibility) {
      if (!visibility) {
        this.selectedDocumentVisibility = { id: 'all', label: 'All documents' }
      }
      const visi = !visibility ? 'all' : visibility.id
      this.updateFilters({ ...this.docsPayload(), visibility: visi })
    },
    filterBySeverity(severity) {
      if (!severity) {
        this.selectedSeverity = { id: null, label: 'All risk levels' }
      }
      const sever = !severity ? null : severity.id
      this.updateFilters({ ...this.docsPayload(), severity: sever })
    },
    filterByReviewStatus(reviewStatus) {
      if (!reviewStatus) {
        this.selectedReviewStatus = { id: null, label: 'All review types' }
      }
      const status = !reviewStatus ? null : reviewStatus.id
      this.updateFilters({
        ...this.docsPayload(),
        review_status: status
      })
    },
    trackSelectedDocs(selected, docId) {
      if (selected) {
        this.selectedDocIds = selected
      } else {
        this.selectedDocIds = this.selectedDocIds.filter((id) => id !== docId)
      }

      if (this.selectedDocIds.length < this.docs.length) {
        this.selectAllDocsOnCurrentPage = false
        this.selectAll = false
      }
    },
    docsPayload() {
      return {
        request_id: this.request.pretty_id,
        search_term: this.docSearchTerm.length > 2 ? this.docSearchTerm : null,
        page_number: this.currentPageNum,
        visibility: this.selectedDocumentVisibility.id,
        severity: this.selectedSeverity.id,
        review_status: this.selectedReviewStatus.id,
        folderless_docs: this.folderlessDocs,
        sort_field: this.sortField,
        sort_order: this.sortOrder,
        ...this.filtersApplied
      }
    },
    refreshDocuments() {
      // Will reapply current filters
      const docsPayload = this.docsPayload()
      this.fetchDocuments(docsPayload)
      this.resetCardSelection()
    },
    resetCardSelection() {
      this.selectAllDocsOnCurrentPage = false
      this.selectAll = false
      this.selectedDocIds = []
      this.singleDocAction = false
      this.setCardListCheckbox(false)
    },
    setCardListCheckbox(val) {
      // Uncheck all the cards in the DOM for the current document list
      const docList = this.$refs.docList
      if (!docList) {
        return
      }
      docList.forEach((listContainer) => {
        const checkboxWrapper = listContainer.$el.childNodes[0]
        const checkbox = checkboxWrapper.childNodes[0]
        checkbox.checked = val
      })
    },
    selectActiveCard(id) {
      this.singleDocAction = true
      this.selectAll = false
      if (!this.selectAllDocsOnCurrentPage) {
        this.onlySelectActiveDoc(id)
      } else {
        this.selectAllDocsOnCurrentPage = false
      }
      this.selectedDocIds = [id]
    },
    onlySelectActiveDoc(id) {
      // Select the active document
      // And unselect the rest
      const docList = this.$refs.docList
      if (!docList) {
        return
      }
      docList.forEach((card) => {
        const checkboxWrapper = card.$el.childNodes[0]
        const checkbox = checkboxWrapper.childNodes[0]
        if (card.id === id) {
          checkbox.checked = true
          return
        }
        checkbox.checked = false
      })
    },
    setSelectAll() {
      this.selectAll = !this.selectAll
    },
    openModal(modalName) {
      if (!this.selectedDocIds.length && !this.selectAll) {
        this.setCurrentAction('docEmpty')
      } else {
        this.setCurrentAction(modalName)
      }
    },
    setPageNum(pageNumber) {
      this.currentPageNum = pageNumber
      const filterData = this.docsPayload()
      this.setDocumentFilters(filterData)
      this.fetchDocuments(filterData)
      this.resetCardSelection()
    },
    closeSortMenu() {
      if (this.sortMenuOpen) {
        this.sortMenuOpen = false
      }
    },
    selectSortField(column) {
      this.sortField = column
      this.closeSortMenu()
    },
    toggleSortOrder() {
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc'
      } else {
        this.sortOrder = 'asc'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../styles/globals.scss';

.documents-tab {
  background: #f4f4f6;
  &-empty {
    background: white;
    margin: 1.5rem 0;
  }
  &-header {
    .column {
      padding: 0.5rem;
    }
    margin: 0;
  }
  &-content {
    &-list {
      .visibility-empty-state {
        padding-bottom: 326px;
      }
    }
    &-columns {
      display: flex;
    }
    .folders-column {
      background: #f4f4f6;
      min-width: 280px;
      max-width: 280px;
      .empty-state {
        font-size: 12px;
        padding-left: 8px;
        padding-right: 8px;
        .conditional {
          padding-top: 18px;
          padding-bottom: 18px;
          color: #6e6e87;
        }
      }
      .folders-header {
        padding: 4px 12px;
      }
      .folders-search {
        padding: 8px;
      }
      .folders-subheader {
        padding: 6px 8px;
      }
      .documents-folders {
        width: 100%;
        .folder-button {
          cursor: pointer;
          padding: 8px;
          width: 100%;
          text-align: left;
          height: 30px;
          .folder-name,
          .caret,
          .folder {
            display: inline-block;
            position: relative;
            top: -5px;
            margin: 0;
          }
          .folder-name {
            top: -10px;
            margin-left: 8px;
          }
          &:hover {
            background: #ebebef;
          }
        }
        .folder-content {
          .folder-button {
            padding-left: 55px;
          }
        }
        .folder-button-active {
          background: white;
        }
      }
    }
    .cards-column {
      background: white;
      max-height: 3140px;
      width: 888px;
      .custom-table-header {
        margin-bottom: 0;
      }

      .search-and-sort-container {
        display: flex;
        align-items: end;

        padding-top: 0.5rem;
        padding-right: 0.75rem;
        padding-left: 0.75rem;
      }

      .document-search {
        padding-right: 4px;
        flex: 1;
      }

      .icons-filters {
        margin-top: 0.25rem;
        .select-list-checkbox {
          padding-left: 0.75rem;
          margin: 0;
        }
        .button.bulk-action {
          padding-left: 0.75rem;
        }
      }
      .search-results-column {
        margin-bottom: 0;
        .level-left {
          width: 100%;
        }
      }
    }
    button.select-all-documents-view-button {
      color: $blue;
      vertical-align: inherit;
      text-decoration: none;
      padding: 0;
      font-size: 0.75rem;
      font-weight: 700;
    }
  }
}

.document-card-border {
  border-bottom: 1px solid #ccc;
}

#documents-sort-menu-wrapper {
  position: relative;

  #documents-sort-menu {
    background: white;
    position: absolute;
    z-index: 2;
    top: calc(100% + 4px);
    right: 0;
    border: 1px solid #d8d8df;
    border-radius: 4px;
    width: 170px;
    .sort-menu-button {
      display: inline-flex;
      text-decoration: none;
      padding: 4px;
      width: 100%;
      .nr-icon {
        margin-top: 4px;
        margin-right: 4px;
      }

      &.selected-sort-field {
        font-weight: bold;
      }
    }
  }
}
</style>
